import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { shouldUseGehaV2Styling } from '../../../../utils/gehaThemeUtils';
import { phoneOnly } from '../../../ConstantsStyles';
import {
  SuggestioContent,
  SuggestionBox,
  SuggestionHeading,
  ViewMoreButton,
} from './PatientReviews.styled';

type SurveyAnswers = {
  count: number;
  title: string;
};
type Props = {
  heading: string;
  surveyAnswers: SurveyAnswers[];
  dataTestId: string;
};

export const PatientReviewSuggestionCard = ({
  heading,
  surveyAnswers,
  dataTestId,
}: Props) => {
  const { t } = useTranslation();
  const mobileScreen = useMediaQuery(phoneOnly);
  const [viewMore, setViewMore] = useState<Boolean>(false);
  const showItems = viewMore ? surveyAnswers?.length : 3;
  const buttonTextColor = '$core.color.brand.60';
  const handleViewMore = () => {
    setViewMore((prev) => !prev);
  };
  const allCountIsZero = surveyAnswers?.every((item) => item.count === 0);
  const sortedData = surveyAnswers?.sort((a, b) => b.count - a.count);
  const isGEHAV2 = shouldUseGehaV2Styling();
  const getViewMoreAriaLabel = (count: number, heading: string) =>
    `${`+${count} ${t('More')}`} ${heading}`;
  return (
    <SuggestionBox color={'white'}>
      <Flex css={{ 'ul > li': { marginTop: '8px' } }} direction={'column'}>
        <SuggestionHeading
          cssProps={{ isGEHAV2 }}
          data-testid={`heading-${heading.replace(/ /g, '-')}`}
          level={3}
        >
          {heading}
        </SuggestionHeading>
        <ul>
          {!allCountIsZero && sortedData?.length ? (
            sortedData.slice(0, showItems).map((item) => (
              <li data-testid={dataTestId} key={item.title}>
                <SuggestioContent
                  color={isGEHAV2 ? '$core.color.brand.100' : ''}
                  size="$md"
                >
                  {t(item.title)} ({item.count})
                </SuggestioContent>
              </li>
            ))
          ) : (
            <li data-testid="no-results">
              <SuggestioContent
                color={isGEHAV2 ? '$core.color.brand.100' : ''}
                size="$md"
              >
                {t('No Results')}
              </SuggestioContent>
            </li>
          )}
        </ul>
        {sortedData?.length > 3 && !allCountIsZero && !viewMore ? (
          <ViewMoreButton
            aria-label={getViewMoreAriaLabel(surveyAnswers.length - 3, heading)}
            data-testid="view-more"
            onClick={handleViewMore}
          >
            <Text color={buttonTextColor} fontSize="md" fontWeight={'bold'}>
              {`+${surveyAnswers.length - 3}  ${t('More')}`}
            </Text>
            {mobileScreen ? (
              <IconSymbol
                color={buttonTextColor}
                icon={'keyboard_arrow_down'}
                size={18}
              />
            ) : null}
          </ViewMoreButton>
        ) : null}
      </Flex>
    </SuggestionBox>
  );
};
